import React, { useEffect, useState, useRef } from 'react';
import { accountAPI, rulesAPI, positionAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://95.179.207.212:8080';
const WS_BASE = API_BASE.replace('/api', '');

interface Rule {
  id: number;
  name: string;
  description?: string;
  thresholdEntry: number;
  thresholdReturn: number;
}


// 숫자 변경 시 글자색만 깜박이는 컴포넌트
const FlashValue: React.FC<{ value: number | null | undefined; format: (v: number) => string; color?: string }> = ({ value, format, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    }
    prevRef.current = value ?? undefined;
  }, [value]);

  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;

  return (
    <span style={{
      color: flash ? '#ffe066' : (color || 'rgba(255,255,255,0.8)'),
      fontWeight: 600,
      transition: 'color 0.1s',
      fontFamily: 'monospace',
    }}>
      {format(value)}
    </span>
  );
};

const RegisterModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [selectedRuleId, setSelectedRuleId] = useState<number | ''>('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 그룹별 계좌 선택
  const [group, setGroup] = useState('');
  const [groupAccounts, setGroupAccounts] = useState<any[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupList, setGroupList] = useState<string[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  // 직접 입력
  const [manualId, setManualId] = useState('');

  useEffect(() => {
    rulesAPI.getAll().then(res => {
      setRules(res.data);
      if (res.data.length > 0) setSelectedRuleId(res.data[0].id);
    }).finally(() => setRulesLoading(false));
    // 그룹 목록 로드
    accountAPI.getGroups().then(res => {
      if (Array.isArray(res.data)) setGroupList(res.data);
    }).catch(() => {});
  }, []);

  const selectedRule = rules.find(r => r.id === selectedRuleId);

  const handleSearchGroup = async () => {
    if (!group.trim()) return;
    setGroupLoading(true);
    setGroupAccounts([]);
    setCheckedIds(new Set());
    try {
      const res = await accountAPI.getAccountsByGroup(group.trim());
      setGroupAccounts(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('그룹 계좌 조회 실패');
    } finally {
      setGroupLoading(false);
    }
  };

  const toggleCheck = (login: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(login) ? next.delete(login) : next.add(login);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === groupAccounts.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(groupAccounts.map((a: any) => a.login)));
    }
  };

  const handleSubmit = async () => {
    if (!selectedRuleId) { setError('적용할 룰을 선택해주세요.'); return; }
    const ids = manualId.trim()
      ? [manualId.trim()]
      : Array.from(checkedIds);
    if (ids.length === 0) { setError('등록할 계좌를 선택해주세요.'); return; }
    setLoading(true);
    setError('');
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await api.post('/v1/accounts', {
          accountId: id,
          isBBook: true,
          isActive: true,
          ruleId: selectedRuleId,
          symbolMode: 'ALL',
        });
        successCount++;
      } catch {
        failCount++;
      }
    }
    setLoading(false);
    if (failCount === 0) {
      onSuccess();
      onClose();
    } else {
      setError(`${successCount}개 성공, ${failCount}개 실패 (이미 등록된 계좌 제외)`);
      onSuccess();
    }
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>B북 계좌 등록</h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={modalStyles.body}>
          {/* 룰 선택 */}
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>전환 룰 선택</label>
            {rulesLoading ? (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>룰 로딩 중...</div>
            ) : (
              <select style={modalStyles.select} value={selectedRuleId} onChange={e => setSelectedRuleId(Number(e.target.value))}>
                {rules.map(rule => <option key={rule.id} value={rule.id}>{rule.name}</option>)}
              </select>
            )}
            {selectedRule && (
              <div style={{ ...modalStyles.ruleInfo, marginTop: '8px' }}>
                <div style={modalStyles.ruleInfoRow}>
                  <span style={modalStyles.ruleInfoLabel}>전환 임계값</span>
                  <span style={modalStyles.ruleInfoValue}>Drawdown {selectedRule.thresholdEntry}% 이하</span>
                </div>
              </div>
            )}
          </div>

          {/* 그룹별 계좌 검색 */}
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>그룹별 계좌 검색</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                style={{ ...modalStyles.select, flex: 1 }}
                value={group}
                onChange={e => setGroup(e.target.value)}
              >
                <option value="">-- 그룹 선택 --</option>
                {groupList.map((g: string) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              <button
                style={{ ...modalStyles.submitBtn, padding: '10px 16px', whiteSpace: 'nowrap' as const }}
                onClick={handleSearchGroup}
                disabled={groupLoading || !group}
              >
                {groupLoading ? '조회중...' : '검색'}
              </button>
            </div>
          </div>

          {/* 계좌 체크박스 리스트 */}
          {groupAccounts.length > 0 && (
            <div style={modalStyles.field}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={modalStyles.label}>계좌 선택 ({checkedIds.size}/{groupAccounts.length})</label>
                <button
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '3px 10px', color: 'rgba(255,255,255,0.6)', fontSize: '12px', cursor: 'pointer' }}
                  onClick={toggleAll}
                >
                  {checkedIds.size === groupAccounts.length ? '전체 해제' : '전체 선택'}
                </button>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                {groupAccounts.map((acc: any) => (
                  <div
                    key={acc.login}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: checkedIds.has(acc.login) ? 'rgba(102,126,234,0.1)' : 'transparent' }}
                    onClick={() => toggleCheck(acc.login)}
                  >
                    <input type="checkbox" checked={checkedIds.has(acc.login)} onChange={() => toggleCheck(acc.login)} style={{ cursor: 'pointer' }} />
                    <span style={{ fontFamily: 'monospace', color: '#667eea', fontSize: '13px', fontWeight: 700 }}>{acc.login}</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{acc.name}</span>
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginLeft: 'auto' }}>{acc.group}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 직접 입력 */}
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>직접 입력 (계좌번호)</label>
            <input
              style={modalStyles.input}
              placeholder="예: 102851 (그룹 선택 없이 직접 입력)"
              value={manualId}
              onChange={e => setManualId(e.target.value)}
            />
          </div>

          {error && <div style={modalStyles.error}>{error}</div>}
        </div>
        <div style={modalStyles.footer}>
          <button style={modalStyles.cancelBtn} onClick={onClose}>취소</button>
          <button
            style={{ ...modalStyles.submitBtn, opacity: (loading || rules.length === 0) ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={loading || rules.length === 0}
          >
            {loading ? '등록 중...' : `등록 ${checkedIds.size > 0 ? `(${checkedIds.size}개)` : manualId ? '(1개)' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const AccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'B_BOOK' | 'A_BOOK'>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [equityMap, setEquityMap] = useState<Record<string, any>>({});
  const [floatingMap, setFloatingMap] = useState<Record<string, any>>({});
  const [aBookSet, setABookSet] = useState<Set<string>>(new Set());
  const [prevFloating, setPrevFloating] = useState<Record<string, number>>({});
  const [flashFloating, setFlashFloating] = useState<Record<string, boolean>>({});
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const ruleId = new URLSearchParams(location.search).get('ruleId');
  const ruleName = ruleId ? accounts.find((a: any) => String(a.ruleId) === ruleId)?.ruleName : null;

  const fetchFloating = () => {
    positionAPI.getFloating().then(res => {
      const newMap: Record<string, any> = {};
      const aBooks = new Set<string>();
      res.data.forEach((p: any) => {
        newMap[p.account_id] = p;
        if (p.book_type === 'A') aBooks.add(p.account_id);
      });
      // 깜박임 처리 - 이전 값과 비교
      setFloatingMap(prev => {
        Object.keys(newMap).forEach(id => {
          const newVal = Number(newMap[id]?.floating_total);
          const oldVal = Number(prev[id]?.floating_total);
          if (prev[id] !== undefined && oldVal !== newVal) {
            setFlashFloating(f => ({ ...f, [id]: true }));
            setTimeout(() => setFlashFloating(f => ({ ...f, [id]: false })), 600);
          }
        });
        // 기존 데이터 유지하면서 새 데이터로 업데이트 (깜박임 방지)
        return { ...prev, ...newMap };
      });
      setABookSet(aBooks);
    }).catch(() => {});
  };

  const fetchAccounts = () => {
    setLoading(true);
    accountAPI.getAll().then(res => {
      setAccounts(res.data);
      // API에서 받은 name/balance/equity/drawdown으로 초기값 설정
      const names: Record<string, string> = {};
      const initEquity: Record<string, any> = {};
      res.data.forEach((acc: any) => {
        if (acc.name) names[acc.accountId] = acc.name;
        if (acc.lastBalance != null || acc.lastEquity != null) {
          initEquity[acc.accountId] = {
            balance: acc.lastBalance,
            equity: acc.lastEquity,
            drawdown: acc.lastDrawdown,
          };
        }
      });
      setNameMap(prev => ({ ...names, ...prev }));
      // WebSocket 데이터가 없으면 DB 값으로 초기화
      setEquityMap(prev => {
        const merged = { ...initEquity };
        Object.keys(prev).forEach(id => { if (prev[id]) merged[id] = prev[id]; });
        return merged;
      });
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAccounts(); fetchFloating(); }, []);
  useEffect(() => { const t = setInterval(fetchFloating, 5000); return () => clearInterval(t); }, []);

  // WebSocket 실시간 연결
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe('/topic/equity-updates', (msg) => {
          const data = JSON.parse(msg.body);
          setEquityMap(prev => ({
            ...prev,
            [data.accountId]: {
              equity: data.equity,
              balance: data.balance,
              drawdown: data.drawdown,
            }
          }));
          if (data.name) {
            setNameMap(prev => ({ ...prev, [data.accountId]: data.name }));
          }
        });
      },
    });
    client.activate();
    return () => { client.deactivate(); };
  }, []);

  const filtered = accounts.filter(acc => {
    const matchSearch = acc.accountId.includes(search) || (acc.mt5Group || '').includes(search);
    const isABook = aBookSet.has(acc.accountId);
    const matchFilter = filter === 'ALL' || (filter === 'B_BOOK' && !isABook) || (filter === 'A_BOOK' && isABook);
    const matchRule = !ruleId || String(acc.ruleId) === ruleId;
    return matchSearch && matchFilter && matchRule;
  });

  const ddColor = (v: number) => {
    if (!v) return 'rgba(255,255,255,0.5)';
    if (v <= -10) return '#ff4757';
    if (v <= -5) return '#ff6b6b';
    if (v < 0) return '#f7971e';
    return '#2ed573';
  };

  return (
    <div style={styles.container}>
      {showModal && (
        <RegisterModal onClose={() => setShowModal(false)} onSuccess={fetchAccounts} />
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>계좌 관리</h1>
          <p style={styles.subtitle}>
            {ruleId && ruleName
              ? `⚙️ ${ruleName} 적용 계좌 — ${filtered.length}개`
              : `총 ${accounts.length}개 계좌`}
          </p>
        </div>
        <button style={styles.registerBtn} onClick={() => setShowModal(true)}>
          + B북 계좌 등록
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="🔍 계좌번호 또는 그룹 검색"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={styles.filterGroup}>
          {(['ALL', 'B_BOOK', 'A_BOOK'] as const).map(f => (
            <button
              key={f}
              style={filter === f ? { ...styles.filterBtn, ...styles.filterBtnActive } : styles.filterBtn}
              onClick={() => setFilter(f)}
            >
              {f === 'ALL' ? '전체' : f === 'B_BOOK' ? '🟢 B북' : '🔴 A북'}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.loading}>로딩 중...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>계좌번호</th>
                <th style={styles.th}>이름</th>
                <th style={styles.th}>구분</th>
                <th style={styles.th}>적용 룰</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.th}>Equity</th>
                <th style={styles.th}>Drawdown</th>
                <th style={styles.th}>Floating</th>
                <th style={styles.th}>전환 임계값</th>
                <th style={styles.th}>복귀 임계값</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>상세</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((acc: any) => {
                const rt = equityMap[acc.accountId];
                return (
                  <tr key={acc.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.accountId}>{acc.accountId}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.name}>{nameMap[acc.accountId] || '-'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={aBookSet.has(acc.accountId) ? styles.badgeA : styles.badgeB}>
                        {aBookSet.has(acc.accountId) ? '🔴 A북' : '🟢 B북'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {acc.ruleName
                        ? <span style={styles.ruleTag}>{acc.ruleName}</span>
                        : <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>미설정</span>}
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={rt?.balance} format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={rt?.equity} format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={rt?.drawdown} format={v => `${v.toFixed(2)}%`} color={ddColor(rt?.drawdown)} />
                    </td>
                    <td style={styles.td}>
                      {floatingMap[acc.accountId] ? (
                        <span style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: flashFloating[acc.accountId] ? '#ffe066' :
                            (floatingMap[acc.accountId].floating_total >= 0 ? '#2ed573' : '#ff6b6b'),
                          transition: 'color 0.1s',
                        }}>
                          ${Number(floatingMap[acc.accountId].floating_total).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          <span style={{fontSize:'11px', color:'rgba(255,255,255,0.4)', marginLeft:'4px'}}>
                            ({floatingMap[acc.accountId].position_count}건)
                          </span>
                        </span>
                      ) : <span style={{color:'rgba(255,255,255,0.2)'}}>-</span>}
                    </td>
                    <td style={styles.td}>{acc.thresholdEntry ?? '-'}%</td>
                    <td style={styles.td}>
                      {(acc.thresholdReturn === 0 || acc.thresholdReturn == null || acc.thresholdReturn === '0.00')
                        ? <span style={{ color: 'rgba(255,255,255,0.3)' }}>청산 시</span>
                        : `${acc.thresholdReturn}%`}
                    </td>
                    <td style={styles.td}>
                      <span style={acc.active ? styles.statusActive : styles.statusInactive}>
                        {acc.active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button style={styles.detailBtn} onClick={() => navigate(`/accounts/${acc.accountId}`)}>
                        상세 →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div style={styles.empty}>조건에 맞는 계좌가 없습니다.</div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  registerBtn: { background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: '10px', padding: '10px 20px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' as const },
  search: { flex: 1, minWidth: '200px', padding: '10px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' },
  filterGroup: { display: 'flex', gap: '6px' },
  filterBtn: { padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },
  filterBtnActive: { background: 'rgba(102,126,234,0.2)', border: '1px solid rgba(102,126,234,0.4)', color: '#667eea', fontWeight: 600 },
  tableWrapper: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'auto', width: '100%' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '14px 16px', textAlign: 'left' as const, fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' as const, borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' as const, background: 'rgba(255,255,255,0.03)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' },
  td: { padding: '13px 16px', fontSize: '13px', verticalAlign: 'middle' as const },
  accountId: { fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, color: '#667eea' },
  name: { color: 'rgba(255,255,255,0.8)', fontSize: '13px' },
  money: { fontFamily: 'monospace', fontSize: '13px', color: 'rgba(255,255,255,0.8)' },
  badgeB: { background: 'rgba(46,213,115,0.15)', color: '#2ed573', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  badgeA: { background: 'rgba(255,107,107,0.15)', color: '#ff6b6b', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  ruleTag: { background: 'rgba(102,126,234,0.15)', color: '#a78bfa', padding: '3px 10px', borderRadius: '6px', fontSize: '12px' },
  statusActive: { color: '#2ed573', fontSize: '12px' },
  statusInactive: { color: 'rgba(255,255,255,0.3)', fontSize: '12px' },
  detailBtn: { background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.25)', borderRadius: '6px', padding: '5px 12px', color: '#667eea', fontSize: '12px', cursor: 'pointer', fontWeight: 600 },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', width: '480px', maxWidth: '90vw' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 0' },
  title: { color: '#fff', fontSize: '20px', fontWeight: 700, margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', padding: '4px' },
  body: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: 500 },
  input: { padding: '12px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' },
  select: { padding: '12px 16px', background: '#1e1e35', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', width: '100%' },
  ruleInfo: { background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.2)', borderRadius: '10px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  ruleInfoRow: { display: 'flex', justifyContent: 'space-between' },
  ruleInfoLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '12px' },
  ruleInfoValue: { color: '#a78bfa', fontSize: '12px', fontWeight: 600 },
  error: { background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#ff6b6b', fontSize: '13px' },
  footer: { display: 'flex', gap: '12px', padding: '0 24px 24px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 24px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer' },
  submitBtn: { padding: '10px 24px', background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
};

export default AccountsPage;
